/**
 * @author Mathieu Chantot <mathieu.chantot@hes-so.ch>
 * @date 01/24
 *
 * @todo look at all the todos
 * @todo configure every transition between states
 * @todo Display warning on screen
 */

import React, { useRef, useState, useEffect } from "react";

import PathControl from "./PathControl.js"; // adjust the path as needed
import PathComputing from "./PathComputing.js";
import MapFeatures from "./mapFeatures.js";
import Map from "./Map.js";
import { sector, coordAll } from "@turf/turf";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "react-notifications/lib/notifications.css";
import "./PathDrawing.css";

import { NotificationContainer, NotificationManager } from "react-notifications";

export default function PathDrawing({ vehicle, sendToInfrastructure }) {
  vehicle.localization = { latitude: 46.79679999, longitude: 7.147255, heading: 0 };

  const turf = { sector, coordAll };

  // Element to be displayed on the map.
  const mapFeatures = useRef(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);

  const [mapMovement, setMapMovement] = useState(true);
  const [drawingMode, setdrawingMode] = useState("");
  const [SmoothDistance, setSmoothDistance] = useState(0.5);
  const [isComputed, setIsComputed] = useState(false);
  const [trajectory, setTrajectory] = useState([]);

  // All those const should be modified when integrating or get from a json file
  const circleRadius = 3;
  const precision = 0.2;
  const maxAngle = 30;
  const turningRadius = 0.1;

  function warning(comments) {
    let children = document.querySelectorAll(".notification-warning");
    children.forEach((child) => {
      child.style.display = "none";
    });
    if (Array.isArray(comments)) {
      console.log("comment", comments);
      comments.forEach(function (comment) {
        NotificationManager.warning(comment, "");
      });
    }
  }

  function success(comment) {
    NotificationManager.success(comment, "Success ", 5000);
  }

  useEffect(() => {
    if (mapFeatures.current === null) {
      mapFeatures.current = new MapFeatures(mapRef);
    }
  }, []);

  /**
   * @brief Setup drawing mode.
   * @param {string} drawingMode - The drawing mode to set
   */
  function drawState(drawingMode) {
    drawRef.current.deleteAll();
    setdrawingMode(drawingMode);
    drawRef.current.changeMode(drawingMode === "dot" ? "draw_line_string" : "draw_paint_mode");
    //disable map mouvement if needed
    if (drawingMode === "line") {
      setMapMovement(false);
    } else {
      setMapMovement(true);
    }
  }

  /**
   * @brief Setup moving mode.
   */
  function moveState() {
    //See if we need it
    //if (!map.current) return; // wait for map to initialize
    setdrawingMode("move");
    drawRef.current.changeMode("static_mode");
    setMapMovement(true);
  }

  /**
   * @brief Manage the computation of the drawing.
   */
  function buildPath() {
    // Prevent sending trajectory while computing
    setIsComputed(false);

    // Go to validation "state"
    setMapMovement(true);
    drawRef.current.changeMode("static_mode");
    setdrawingMode("move");

    //Features to be displayed
    let trajectory = [];
    let computedTrajectory = [];
    var limits = [];

    //Extract feature from map
    const features = drawRef.current.getAll();
    if (features.features.length === 0) {
      console.log("No path to compute");
      setTrajectory([]);
      return;
    }

    //Extract coordinates from features
    trajectory = turf.coordAll(features);
    if (trajectory.length < 2) {
      console.log("Not enough points to compute");
      return;
    }

    // Remove previously computed features
    mapFeatures.current.deleteFeatures();

    // Compute every features
    var pathComputing = new PathComputing(
      precision,
      {
        lng: vehicle.localization.longitude,
        lat: vehicle.localization.latitude,
        heading: vehicle.localization.heading,
      },
      circleRadius,
      maxAngle,
      turningRadius,
      SmoothDistance
    );
    let result = pathComputing.computeTrajectory(trajectory.map((innerArray) => [...innerArray]));
    //Check if a warning has been raised
    if (!result) {
      console.warn("There is a problem with the trajectory");
      return;
    }
    let comment = [];
    [computedTrajectory, limits, comment] = result;

    //Display computed features
    mapFeatures.current.addFeature(trajectory, "line", "#00ffff", "solid");
    mapFeatures.current.addFeature(computedTrajectory, "line", "#64fc3a", "solid");
    //@todo use for each
    for (let i = 0; i < limits.length; i++) {
      mapFeatures.current.addFeature(limits[i], "line", "#64fc3a", "dotted");
    }

    //if (comment.length === 0){
    // Enable data sending
    console.log("computedTrajectory", computedTrajectory);
    setTrajectory(computedTrajectory.map((innerArray) => [...innerArray]));
    setIsComputed(true);
    //}else{
    warning(comment);
    //}
  }

  useEffect(() => {
    if (isComputed) {
      sendTrajectory();
    }
    setIsComputed(false);
  }, [trajectory]);

  function sendTrajectory() {
    console.log(trajectory);
    // Send the trajectory to the vehicle
    if (trajectory.length !== 0) {
      sendToInfrastructure(vehicle.id, "frontendPath", { trajectory: trajectory });
      success("Path sent");
    } else {
      warning("No path to send");
    }
  }

  function prerecorded() {
    sendToInfrastructure(vehicle.id, "frontendPath", { prerecorded: true });
    setIsComputed(false);
    drawRef.current.deleteAll();
    mapFeatures.current.deleteFeatures();
    success("Prerecorded path sent.");

    /**
		 * This code can be usefull for future implementation of prerecorded path	display.
		setdrawingMode('move');
		setIsComputed(true);
		setMapMovement(true);
		console.log("prerecordedPath", prerecordedPath);
		if(prerecordedPath.length === 0){
			warning(["No prerecorded path"]);
			return;
		}else{
			mapFeatures.current.deleteFeatures();
			mapFeatures.current.addFeature(prerecordedPath,"line","#0000FF","solid");
			setTrajectory(prerecordedPath);
		}**/
  }

  function erase() {
    setIsComputed(false);
    mapFeatures.current.deleteFeatures();
    drawRef.current.deleteAll();
    drawState(drawingMode);
  }

  return (
    <div id="pathDrawing">
      <NotificationContainer id="notif" />
      <PathControl
        drawingMode={drawingMode}
        moveState={moveState}
        drawState={drawState}
        prerecorded={prerecorded}
        validation={buildPath}
        smoothDistance={SmoothDistance}
        setSmoothDistance={setSmoothDistance}
        isComputed={isComputed}
        sendTrajectory={sendTrajectory}
        erase={erase}
      />
      <Map
        id="map"
        map={mapRef}
        draw={drawRef}
        vehicle={vehicle}
        mapMovement={mapMovement}
        maxAngle={maxAngle}
        circleRadius={circleRadius}
      />
    </div>
  );
}
